import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider = ({ children, user }) => {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const [tourStatusChecked, setTourStatusChecked] = useState(false);

  // Check if user has completed tour before - only auto-start if user is logged in
  useEffect(() => {
    // Only show tour if user is logged in
    if (!user) {
      return;
    }

    // Prevent auto-starting multiple times in the same session
    if (hasAutoStarted || tourStatusChecked) {
      return;
    }

    // Check tour status from backend
    const checkTourStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/tour/status`, { 
          withCredentials: true 
        });
        
        setTourStatusChecked(true);
        
        // If tour is already completed, don't show it
        if (response.data.tour_completed) {
          return;
        }

        // Auto-start tour for first-time users after a short delay
        const timer = setTimeout(() => {
          setIsTourActive(true);
          setHasAutoStarted(true);
        }, 1000);
        return () => clearTimeout(timer);
      } catch (error) {
        console.error('Error checking tour status:', error);
        // On error, don't show tour
        setTourStatusChecked(true);
      }
    };

    checkTourStatus();
  }, [user, hasAutoStarted, tourStatusChecked]);

  const startTour = () => {
    setCurrentStep(0);
    setIsTourActive(true);
    setHasAutoStarted(true); // Prevent auto-restart after manual tour
  };

  const endTour = async () => {
    setIsTourActive(false);
    setCurrentStep(0);

    // Save tour completion to backend
    try {
      await axios.post(`${API}/auth/tour/complete`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Error saving tour completion:', error);
    }

    // Notify any listeners (e.g. ShowFinderProvider auto-launches the wizard)
    try {
      window.dispatchEvent(new CustomEvent('baadtv:tour-ended'));
    } catch (e) { /* SSR-safe noop */ }
  };

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const skipTour = () => {
    endTour();
  };

  return (
    <TourContext.Provider
      value={{
        isTourActive,
        currentStep,
        startTour,
        endTour,
        nextStep,
        prevStep,
        skipTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
